import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from "../../firebase"; 
import { collection, getDocs, addDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../auth/AuthContext';

function AddGroceryProduct() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const isEditing = Boolean(productId);
  const { hasPermission } = useAuth();

  // Form State
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  
  // --- NEW: State for categories ---
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // UI State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // --- NEW: Fetch categories for the dropdown ---
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'Category meals'));
        const categoriesList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));
        setCategories(categoriesList);
      } catch (err) {
        console.error("Error fetching categories: ", err);
        setError('Failed to load categories.');
      }
    };
    fetchCategories();
  }, []);

  // Fetch product data if we are in edit mode
  useEffect(() => {
    const fetchProduct = async () => {
      if (!isEditing) {
        setLoading(false);
        return;
      }
      try {
        // Use the correct collection name 'MealsProducts'
        const docRef = doc(db, 'MealsProducts', productId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setName(data.name || '');
          setPrice(data.price || '');
          setQuantity(data.quantity || '');
          setImageBase64(data.imageUrl || '');
          setSelectedCategory(data.categoryId || ''); // Set the selected category ID
        } else {
          setError('Product not found.');
        }
      } catch (err) {
        setError('Failed to load product data.');
      } finally {
        setLoading(false);
      }
    };
    // Wait until categories are loaded before trying to fetch product data
    if (categories.length > 0) {
        fetchProduct();
    } else if (!isEditing) {
        setLoading(false);
    }
  }, [productId, isEditing, categories]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 750 * 1024) {
      setError('File is too large (max 750KB).');
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setImageBase64(reader.result);
    reader.onerror = () => setError("Failed to read image file.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing && !hasPermission('grocery_products.update')) {
      return setError("You don't have permission to update products.");
    }
    if (!isEditing && !hasPermission('grocery_products.create')) {
      return setError("You don't have permission to create products.");
    }
    if (!name || !price || !quantity || !selectedCategory) {
      setError('All fields including category are required.');
      return;
    }

    setLoading(true);
    const categoryObject = categories.find(c => c.id === selectedCategory);

    const productData = {
      name,
      price: parseFloat(price),
      quantity: parseInt(quantity, 10),
      imageUrl: imageBase64,
      categoryId: selectedCategory, // Store the category document ID
      categoryName: categoryObject ? categoryObject.name : '', // Store the category name for easy display
      updatedAt: serverTimestamp(),
    };

    try {
      if (isEditing) {
        const docRef = doc(db, 'MealsProducts', productId);
        await updateDoc(docRef, productData);
        alert('Product updated successfully!');
      } else {
        productData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'MealsProducts'), productData);
        alert('Product created successfully!');
      }
      navigate('/groceryproduct');
    } catch (err) {
      setError('Failed to save product.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{textAlign: 'center', padding: '2rem'}}>Loading...</div>;

  return (
    <div style={styles.formContainer}>
      <h2>{isEditing ? 'Edit Grocery Product' : 'Add New Grocery Product'}</h2>
      <form onSubmit={handleSubmit}>
        {error && <p style={styles.error}>{error}</p>}
        
        <div style={styles.formGroup}>
          <label>Category</label>
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} style={styles.input} required>
            <option value="">-- Select a Category --</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div style={styles.formGroup}>
          <label>Product Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={styles.input} required />
        </div>
        <div style={styles.formGroup}>
          <label>Price</label>
          <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} style={styles.input} required />
        </div>
        <div style={styles.formGroup}>
          <label>Quantity</label>
          <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} style={styles.input} required />
        </div>
        <div style={styles.formGroup}>
          <label>Image</label>
          <input type="file" accept="image/*" onChange={handleImageChange} style={styles.input} required={!isEditing} />
        </div>
        {imageBase64 && <img src={imageBase64} alt="Preview" style={styles.previewImage} />}
        
        <button type="submit" style={styles.submitButton} disabled={loading}>
          {loading ? 'Saving...' : (isEditing ? 'Update Product' : 'Add Product')}
        </button>
      </form>
    </div>
  );
}

// --- STYLES ---
const styles = {
  formContainer: { padding: '2rem', maxWidth: '600px', margin: '0 auto' },
  formGroup: { marginBottom: '1rem' },
  input: { width: '100%', padding: '10px', boxSizing: 'border-box', borderRadius: '5px', border: '1px solid #ccc' },
  submitButton: { padding: '12px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px' },
  error: { color: 'red', marginBottom: '1rem' },
  previewImage: { marginTop: '1rem', maxWidth: '100px', height: '100px', objectFit: 'cover', border: '1px solid #ddd', borderRadius: '5px' }
};

export default AddGroceryProduct;